import React, { useEffect, useRef, useState } from 'react';
import './code.input.scss';

const EMPTY = ['', '', '', ''];

const CodeInput = ({ onComplete, onEdit, invalid = false, busy = false }) => {

  const [ digits, setDigits ] = useState(() => EMPTY.slice());
  const digitsRef = useRef(EMPTY.slice());
  const lastSent = useRef('');
  const busyRef = useRef(busy);
  const inputs = [ useRef(null), useRef(null), useRef(null), useRef(null) ];

  useEffect(() => {

    busyRef.current = busy;
    if (!busy) trySend(digitsRef.current);

  }, [ busy ]);

  useEffect(() => {

    focusAt(0);

  }, []);

  function focusAt(index) {

    const node = inputs[index] && inputs[index].current;
    if (!node) return;
    node.focus({ preventScroll: true });
    const end = String(node.value || '').length;
    try { node.setSelectionRange(end, end); } catch (err) { /* ignore */ }

  }

  function trySend(next) {

    const code = next.join('');
    if (code.length < 4) {
      lastSent.current = '';
      return;
    }
    if (busyRef.current) return;
    if (lastSent.current === code) return;
    if (onComplete(code) === false) return;
    lastSent.current = code;

  }

  function commit(next) {

    digitsRef.current = next;
    setDigits(next);
    if (onEdit) onEdit();
    trySend(next);

  }

  function putDigit(index, digit) {

    const next = digitsRef.current.slice();
    next[index] = digit;
    commit(next);
    if (index < 3) focusAt(index + 1);
    else focusAt(index);

  }

  function onChange(index, value) {

    const only = String(value).replace(/\D/g, '');
    if (!only) {
      const next = digitsRef.current.slice();
      next[index] = '';
      commit(next);
      return;
    }
    if (only.length > 1) {
      const next = EMPTY.slice();
      only.slice(0, 4).split('').forEach((char, i) => { next[i] = char; });
      commit(next);
      focusAt(Math.min(only.length, 4) - 1);
      return;
    }
    putDigit(index, only);

  }

  function onKeyDown(index, event) {

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusAt(index - 1);
      return;
    }
    if (event.key === 'ArrowRight' && index < 3) {
      event.preventDefault();
      focusAt(index + 1);
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = digitsRef.current.slice();
      if (next[index]) {
        next[index] = '';
        commit(next);
        focusAt(index > 0 ? index - 1 : index);
      } else if (index > 0) {
        next[index - 1] = '';
        commit(next);
        focusAt(index - 1);
      }
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      if (!digitsRef.current[index]) return;
      const next = digitsRef.current.slice();
      next[index] = '';
      commit(next);
      focusAt(index);
      return;
    }

    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      putDigit(index, event.key);
    }

  }

  function onPaste(event) {

    event.preventDefault();
    const text = event.clipboardData ? event.clipboardData.getData('text') : '';
    const only = String(text).replace(/\D/g, '').slice(0, 4);
    const next = EMPTY.slice();
    only.split('').forEach((char, i) => { next[i] = char; });
    commit(next);
    focusAt(Math.min(Math.max(only.length, 1), 4) - 1);

  }

  return (

    <div className={`code-input${ invalid ? ' code-input--invalid' : '' }`}>

      { digits.map((digit, index) => (

        <input

          key={ index }
          ref={ inputs[index] }
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={ 1 }
          value={ digit }
          onChange={ (event) => onChange(index, event.target.value) }
          onKeyDown={ (event) => onKeyDown(index, event) }
          onPaste={ onPaste }
          onFocus={ () => focusAt(index) }
          aria-label={ `Цифра ${ index + 1 }` }

        />

      )) }

    </div>

  );

};

export default CodeInput;
